import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';

import useNLPSearch from 'app/components/NLPSearch/hooks/useNLPSearch';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import useBehavioral, { BEHAVIORALS } from 'hooks/useBehavioral';
import useWindowDimensions from 'hooks/useWindowDimensions';
import {
  isNLPLoadingSelector,
  promptSelector,
  setFeedback,
  setFound,
  setIsNLPLoading,
  setNotFound,
  setPrompt,
  setPromptUuid,
  setShowNLPError,
  setShowNoResults,
  showNoResultsSelector,
} from 'store/reducers/nlp/nlp-reducer';
import { showingLoadingScreenSelector } from 'store/reducers/people-steps-reducer/people-steps-reducer';
import {
  contextSelector,
  currentStepSelector,
  emptyAllFilters,
  IPeopleFilterType,
  isPeopleLoadingSelector,
  PEOPLE_CONTEXT,
  PEOPLE_FILTER_TYPE,
  previousFiltersLengthSelector,
  previousFiltersSelector,
  setCurrentStep,
  setDetails,
  setFilters,
  setIsPeopleLoading,
  setNLPPeopleResults,
  setPeople,
  setSelectedCriteria,
  setShowBackButton,
  setUuids,
  updatePreviousFilters,
} from 'store/reducers/people/people-reducer';
import { ThemeProvider } from 'styled-components';
import { shouldDisableAlternativesForFound } from 'utils/people-alternatives';

import PeopleSearchBar from './PeopleSearchBar';
import PeopleSearchBarHistory from './PeopleSearchBar/PeopleSearchBarHistory';
import { PeopleSearchContext, PeopleSearchInfo } from './PeopleSearchContext';
import PeopleSearchTablet from './PeopleSearchTablet';
import { PeopleSearchWrapper } from './styled';
import usePeopleSearchHistory from './usePeopleSearchHistory';

export default function PeopleSearch() {
  const dimensions = useWindowDimensions();
  const dispatch = useDispatch();
  const showNoResults = useSelector(showNoResultsSelector);
  const { saveSearch, getSearches } = usePeopleSearchHistory();
  const collectiveIndexNlpSearchClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.COLLECTIVE_INDEX_NLP_SEARCH_CLICKED
  );
  const teamingNlpSearchClickedBehavioralFunction = useBehavioral(
    BEHAVIORALS.TEAMING_NLP_SEARCH_CLICKED
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchWrapperRef = useRef<HTMLDivElement>(null);
  const context = useSelector(contextSelector);
  const previousFilters = useSelector(previousFiltersSelector);
  const prompt = useSelector(promptSelector);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>(() => {
    if (prompt) {
      return prompt;
    }

    return '';
  });
  const [currentFilter, setCurrentFilter] = useState<IPeopleFilterType>(null);
  const [searchFocused, setSearchFocused] = useState<boolean>(false);
  const [tabletSearchFocused, setTabletSearchFocused] = useState<boolean>(false);
  const [query, setQuery] = useState<string>('');
  const nlpSearchQuery = useNLPSearch(
    {
      query,
      request_origin:
        context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY ? 'collective_directory' : 'teaming',
    },
    { enabled: query !== '', cacheTime: 0 }
  );
  const currentStep = useSelector(currentStepSelector);

  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isPeopleLoading = useSelector(isPeopleLoadingSelector);
  const showingLoadingScreen = useSelector(showingLoadingScreenSelector);

  const isTablet = useMemo(() => {
    return dimensions && dimensions.width <= 1180;
  }, [dimensions]);

  useOnClickOutside(searchWrapperRef, (e) => {
    const history = document.querySelector('[data-id="people-search-bar-history"]');

    if (history && !history.contains(e.target as Node)) {
      setShowHistory(false);
    }
  });

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isPeopleLoading && filters > 0);
  }, [isNlpLoading, isPeopleLoading, filters]);

  const showLoadingScreen = useMemo<boolean>(() => {
    return isNlpLoading || showingLoadingScreen;
  }, [isNlpLoading, isPeopleLoading, filters, showingLoadingScreen]);

  useEffect(() => {
    if (isLoading || showLoadingScreen) {
      setShowHistory(false);
    }
  }, [isLoading, showLoadingScreen]);

  const sendSearch = (search: string = null) => {
    const prompt = search || searchValue;

    if (prompt && prompt !== query) {
      saveSearch(prompt);
      dispatch(setShowBackButton(false));
      dispatch(setCurrentStep(1));
      dispatch(setIsPeopleLoading(true));
      setQuery(prompt);

      if (showHistory) {
        setShowHistory(false);
      }
    }
  };

  useEffect(() => {
    if (searchFocused && currentStep === 1) {
      if (context === PEOPLE_CONTEXT.COLLECTIVE_DIRECTORY) {
        collectiveIndexNlpSearchClickedBehavioralFunction();
      } else {
        teamingNlpSearchClickedBehavioralFunction();
      }
    }

    if (currentStep !== 1) {
      setSearchFocused(false);
      setTabletSearchFocused(false);
    }
  }, [searchFocused, context, currentStep]);

  useEffect(() => {
    const searches = getSearches();
    if (searchFocused && searches.length > 0 && !isLoading && !showLoadingScreen) {
      setShowHistory(true);
    }
  }, [searchFocused, currentStep, isLoading, showLoadingScreen]);

  useLayoutEffect(() => {
    if (prompt === null) {
      setQuery('');
      setSearchValue('');
    } else {
      setSearchValue(prompt);
      setQuery(prompt);
    }
  }, [prompt]);

  useEffect(() => {
    return () => {
      setQuery('');
      setSearchValue('');
    };
  }, []);

  const openTabletSearch = () => {
    if (isTablet) {
      setTabletSearchFocused(true);
    }
  };

  const closeTabletSearch = () => {
    setTabletSearchFocused(false);
  };

  const initialValues = useMemo<PeopleSearchInfo>(
    () => ({
      searchValue,
      currentFilter,
      searchFocused,
      nlpSearchQuery,
      searchInputRef,
      showHistory,
      isLoading,
      showLoadingScreen,
      setShowHistory,
      sendSearch,
      setSearchValue,
      setCurrentFilter,
      setSearchFocused,
      openTabletSearch,
      closeTabletSearch,
    }),
    [
      searchValue,
      currentFilter,
      searchFocused,
      searchInputRef,
      nlpSearchQuery.data,
      nlpSearchQuery.isLoading,
      nlpSearchQuery.isError,
      showHistory,
      setShowHistory,
      isLoading,
      showLoadingScreen,
    ]
  );

  const arePreviousAndCurrentFiltersEqual = (filters) => {
    // Check if the previous filters are the same as the current filters
    const previousFiltersKeys = Object.keys(previousFilters)
      .filter((key) => key !== PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS)
      .filter((key) => previousFilters[key].length > 0 || previousFilters[key] === true);
    const filtersKeys = Object.keys(filters).filter(
      (key) => key !== PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS
    );

    if (previousFiltersKeys.length !== filtersKeys.length) {
      return false;
    }

    for (const key of previousFiltersKeys) {
      if (!filtersKeys.includes(key)) {
        return false;
      }

      const previousFiltersValues = previousFilters[key];
      const filtersValues = filters[key];

      if (previousFiltersValues.length !== filtersValues.length) {
        return false;
      }

      for (const value of previousFiltersValues) {
        if (filtersValues.find((filter) => filter.uuid === value.uuid) === undefined) {
          return false;
        }
      }
    }

    return true;
  };

  useLayoutEffect(() => {
    dispatch(setIsNLPLoading(nlpSearchQuery.isLoading));
  }, [nlpSearchQuery.isLoading]);

  useEffect(() => {
    const { data, isLoading, isError } = nlpSearchQuery;

    if (data && !isLoading && !isError) {
      const alternativesDisabled = shouldDisableAlternativesForFound(data.found);

      dispatch(setSelectedCriteria(null));
      dispatch(setPrompt(query));
      dispatch(setPromptUuid(data.search_term_prompt));
      dispatch(setFeedback(null));

      if (Object.keys(data.found).length > 0) {
        const exclusiveFilters =
          context === PEOPLE_CONTEXT.TEAMING ? { [PEOPLE_FILTER_TYPE.ONLY_COLLEAGUES]: true } : {};

        // all data.found data except people
        const filtersKeys = Object.keys(data.found).filter(
          (key) => key !== 'people' && key !== PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS
        );
        const filters = filtersKeys.reduce((acc, key) => {
          acc[key] = data.found[key];
          return acc;
        }, {});

        const finalFilters = {
          filters: {
            ...filters,
            [PEOPLE_FILTER_TYPE.ACCOUNT_HOLDERS]: data.found?.account_holders || false,
          },
          exclusive_filters: exclusiveFilters,
        };

        if (showNoResults) {
          dispatch(setShowNoResults(false));
        }

        dispatch(setFilters(finalFilters));
        if (data.found.people) {
          dispatch(setNLPPeopleResults(data.found.people));
        } else {
          dispatch(setNLPPeopleResults([]));
        }
        dispatch(setFound(data.found));
        dispatch(updatePreviousFilters({}));

        if (arePreviousAndCurrentFiltersEqual(data.found)) {
          dispatch(setIsPeopleLoading(false));
          dispatch(setCurrentStep(alternativesDisabled ? 4 : 2));
        }

        if (Object.keys(data.found).length === 1 && data.found.people) {
          dispatch(setCurrentStep(alternativesDisabled ? 4 : 2));
          dispatch(setIsPeopleLoading(false));
        }
      } else {
        dispatch(setShowNoResults(true));
        dispatch(setFound(null));
        dispatch(emptyAllFilters({}));
        dispatch(setPeople([]));
        dispatch(setUuids([]));
        dispatch(
          setDetails({
            company_freelancers: 0,
            cross_kyu_teaming: 0,
            my_company: 0,
          })
        );
        dispatch(setCurrentStep(4));
      }

      if (Object.keys(data.not_found).length > 0) {
        dispatch(setNotFound(data.not_found));
        dispatch(updatePreviousFilters({}));
      } else {
        dispatch(setNotFound(null));
        dispatch(updatePreviousFilters({}));
      }

      dispatch(setIsNLPLoading(false));
    }
  }, [nlpSearchQuery.data, nlpSearchQuery.isLoading]);

  useEffect(() => {
    if (nlpSearchQuery.isError) {
      dispatch(setShowNLPError(true));
      dispatch(setCurrentStep(4));
    }
  }, [nlpSearchQuery.isError]);

  const handleWrapperClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isTablet) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <ThemeProvider
      theme={{
        hasSearchValue: searchValue !== '',
        currentStep,
        isFocused: searchFocused,
        isHistoryOpen: showHistory,
        isTablet,
      }}
    >
      <PeopleSearchContext.Provider value={initialValues}>
        <PeopleSearchWrapper
          ref={searchWrapperRef}
          data-id="people-search-wrapper"
          onClick={handleWrapperClick}
        >
          {isTablet && currentStep === 1 && !isLoading && !showLoadingScreen ? (
            <PeopleSearchBar.Tablet />
          ) : (
            <PeopleSearchBar />
          )}
          <div style={{ position: 'relative', width: '100%' }}>
            <AnimatePresence>{showHistory ? <PeopleSearchBarHistory /> : null}</AnimatePresence>
          </div>
        </PeopleSearchWrapper>
        {ReactDOM.createPortal(
          <AnimatePresence>
            {isTablet &&
            tabletSearchFocused &&
            currentStep === 1 &&
            !isLoading &&
            !showLoadingScreen ? (
              <PeopleSearchTablet />
            ) : null}
          </AnimatePresence>,
          document.body
        )}
      </PeopleSearchContext.Provider>
    </ThemeProvider>
  );
}
